/**
 * Cost Parameters default data and types.
 * Main 5 categories + group6 (single additional-job group).
 */
export const MAIN_CATEGORY_TABS = [
    { id: "main_job", label: "Main Job" },
    { id: "additional_job", label: "Additional Works" },
];
export const GROUP6_CATEGORIES = ["group6"];
export const WORK_DETAILS_CATEGORY_TABS = [
    { id: "compliance", label: "Compliance/Startup" },
    { id: "service_trades", label: "Service Trades" },
    { id: "flooring", label: "Flooring" },
    { id: "internal_demolition", label: "Internal Demolition" },
    { id: "preliminary_cost", label: "Preliminary Cost" },
];
export const UNIT_OPTIONS = {
    standard: ["m2", "m3", "lump sum", "per day"],
    withEach: ["m2", "m3", "lump sum", "per day", "each"],
    lumpSumFirst: ["lump sum", "m2", "per day"],
    eachFirst: ["each", "m2", "lump sum", "per day"],
    perDayFirst: ["per day", "each", "m2", "lump sum"],
    linearMetres: ["m2", "lineal metres", "lump sum"],
    perUnit: ["per unit", "each", "lump sum"],
    percent: ["percent", "lump sum"],
};
const GROUP6_CATEGORY_PREFIX = "flooring_additional_";
const PLASTERING_SECTION_PREFIX = "plastering_";
const OTHER_SECTION_PREFIX = "other_";
/** Section ID prefix used when saving to group6 so load can round-trip. */
export function getGroup6SectionIdPrefix(category) {
    switch (category) {
        case "flooring_additional":
            return GROUP6_CATEGORY_PREFIX;
        case "plastering":
            return PLASTERING_SECTION_PREFIX;
        case "other":
            return OTHER_SECTION_PREFIX;
    }
}
/** Parse group6 section id to get category; returns null if not a group6 section. */
export function getGroup6CategoryFromSectionId(sectionId) {
    if (sectionId.startsWith(GROUP6_CATEGORY_PREFIX))
        return "flooring_additional";
    if (sectionId.startsWith(PLASTERING_SECTION_PREFIX))
        return "plastering";
    if (sectionId.startsWith(OTHER_SECTION_PREFIX))
        return "other";
    return null;
}
/** Strip group6 prefix to get local sectionId for merging. */
export function stripGroup6SectionIdPrefix(sectionId, category) {
    const prefix = getGroup6SectionIdPrefix(category);
    return sectionId.startsWith(prefix)
        ? sectionId.slice(prefix.length)
        : sectionId;
}
/**
 * Main Job categories (group1–group5) that get a client-editable "Other"
 * accordion item. Compliance/Startup is intentionally excluded.
 */
export const MAIN_OTHER_CATEGORIES = [
    "service_trades",
    "flooring",
    "internal_demolition",
    "preliminary_cost",
];
/** Accordion item id used for the editable "Other" section everywhere. */
export const OTHER_ACCORDION_ITEM_ID = "other";
/**
 * Canonical unit-of-measure options for editable "Other" rows. The backend
 * does NOT persist unitOptions — it regenerates them from the stored unit
 * enum on GET — so the frontend owns this list to keep the dropdown stable
 * across add / save / reload. selectedUnit still round-trips for known units.
 */
export const OTHER_UNIT_OPTIONS = UNIT_OPTIONS.withEach;
/**
 * Section ID prefix used when saving a Main Job "Other" section so it can be
 * told apart from seeded sections on load and so its (SectionId, ItemId) does
 * not collide with another group's "Other" on the backend (the backend upsert
 * key is not scoped by group). The prefix carries the category to keep each
 * group's section id unique.
 */
export function getMainOtherSectionPrefix(category) {
    return `${OTHER_SECTION_PREFIX}${category}_`;
}
/** Strip the Main Job "Other" prefix to get the local sectionId for merging. */
export function stripMainOtherSectionPrefix(sectionId, category) {
    const prefix = getMainOtherSectionPrefix(category);
    return sectionId.startsWith(prefix)
        ? sectionId.slice(prefix.length)
        : sectionId;
}
/** Default editable "Other" accordion item for a Main Job category. */
function getDefaultOtherItem(category) {
    return {
        id: OTHER_ACCORDION_ITEM_ID,
        title: "Other",
        sections: [
            {
                sectionId: "placeholder",
                items: [
                    {
                        id: `other-placeholder-${category}`,
                        title: "Work detail in discussion",
                        unitOptions: OTHER_UNIT_OPTIONS,
                        selectedUnit: "lump sum",
                        rate: 0,
                    },
                ],
            },
        ],
    };
}
/** Section ID prefix for group6 save, derived from accordion item id. */
export function getGroup6SectionIdPrefixForAccordionItem(itemId) {
    if (itemId === "floor-finishes" ||
        itemId === "screeds" ||
        itemId === "grinding")
        return GROUP6_CATEGORY_PREFIX;
    if (itemId === "plastering")
        return PLASTERING_SECTION_PREFIX;
    if (itemId === "other")
        return OTHER_SECTION_PREFIX;
    return GROUP6_CATEGORY_PREFIX;
}
/** Default group6 data as single array (flooring additional + plastering + other). */
function getDefaultGroup6() {
    return [
        {
            id: "floor-finishes",
            title: "Floor Finishes",
            sections: [
                {
                    sectionId: "floor-finishes",
                    items: [
                        {
                            id: "add-ff-1",
                            title: "Remove Ceramic Tiles (Direct Stick)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-ff-2",
                            title: "Remove Vinyl (Walk behind)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-ff-3",
                            title: "Remove Carpet Tiles",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-ff-4",
                            title: "Remove Timber / Parquetry (Direct Stick)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-ff-5",
                            title: "After Hours Works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "screeds",
            title: "Screeds",
            sections: [
                {
                    sectionId: "screeds",
                    items: [
                        {
                            id: "add-screeds-1",
                            title: "0mm - 50mm",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-screeds-2",
                            title: "50mm - 80mm",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-screeds-3",
                            title: "After Hours works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "grinding",
            title: "Grinding",
            sections: [
                {
                    sectionId: "grinding",
                    items: [
                        {
                            id: "add-grinding-1",
                            title: "Tenancy Floor Grinding",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "add-grinding-2",
                            title: "After Hours works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "plastering",
            title: "Plastering",
            sections: [
                {
                    sectionId: "placeholder",
                    items: [
                        {
                            id: "plastering-placeholder-item",
                            title: "Work detail in discussion",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "other",
            title: "Other",
            sections: [
                {
                    sectionId: "placeholder",
                    items: [
                        {
                            id: "other-placeholder-item",
                            title: "Work detail in discussion",
                            unitOptions: UNIT_OPTIONS.withEach,
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
    ];
}
function getDefaultFlooring() {
    return [
        {
            id: "floor-finishes",
            title: "Floor Finishes",
            sections: [
                {
                    sectionId: "floor-finishes",
                    items: [
                        {
                            id: "ff-1",
                            title: "Remove Ceramic Tiles (Direct Stick)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ff-2",
                            title: "Remove Vinyl (Walk behind)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ff-3",
                            title: "Remove Carpet Tiles",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ff-4",
                            title: "Remove Timber / Parquetry (Direct Stick)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ff-5",
                            title: "After Hours Works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "screeds",
            title: "Screeds",
            sections: [
                {
                    sectionId: "screeds",
                    items: [
                        {
                            id: "screeds-1",
                            title: "0mm - 50mm",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "screeds-2",
                            title: "50mm - 80mm",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "screeds-3",
                            title: "After Hours works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "grinding",
            title: "Grinding",
            sections: [
                {
                    sectionId: "grinding",
                    items: [
                        {
                            id: "grinding-1",
                            title: "Tenancy Floor Grinding",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "grinding-2",
                            title: "After Hours works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
    ];
}
export function getDefaultCostData() {
    return {
        compliance: getDefaultCompliance(),
        service_trades: [
            ...getDefaultServiceTrades(),
            getDefaultOtherItem("service_trades"),
        ],
        flooring: [...getDefaultFlooring(), getDefaultOtherItem("flooring")],
        internal_demolition: [
            ...getDefaultInternalDemolition(),
            getDefaultOtherItem("internal_demolition"),
        ],
        preliminary_cost: [
            ...getDefaultPreliminaryCost(),
            getDefaultOtherItem("preliminary_cost"),
        ],
        group6: getDefaultGroup6(),
    };
}
function getDefaultCompliance() {
    return [
        {
            id: "demolition",
            title: "Demolition",
            sections: [
                {
                    sectionId: "hoardings",
                    sectionTitle: "Hoardings (note rates are on m2 basis)",
                    items: [
                        {
                            id: "hoarding-internal-solid",
                            title: "Internal Solid Hoarding",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "hoarding-kiosk",
                            title: "Kiosk Hoarding",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "compliance-main",
            title: "Compliance",
            sections: [
                {
                    sectionId: "permits",
                    items: [
                        {
                            id: "comp-demolition-permit",
                            title: "Demolition Permit",
                            unitOptions: UNIT_OPTIONS.lumpSumFirst,
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                        {
                            id: "comp-building-permit",
                            title: "Building Permit",
                            unitOptions: UNIT_OPTIONS.lumpSumFirst,
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
    ];
}
function getDefaultServiceTrades() {
    return [
        {
            id: "services-electrical",
            title: "Services - Electrical",
            sections: [
                {
                    sectionId: "electrical",
                    items: [
                        {
                            id: "elec-1",
                            title: "Isolate and Make Safe Tenancy Single EDB and IDF",
                            unitOptions: UNIT_OPTIONS.lumpSumFirst,
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                        {
                            id: "elec-2",
                            title: "Isolate and Make Safe Tenancy Dual EDB and IDF",
                            unitOptions: UNIT_OPTIONS.lumpSumFirst,
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "fire",
            title: "Fire",
            sections: [
                {
                    sectionId: "fire",
                    showQty: true,
                    items: [
                        {
                            id: "fire-1",
                            title: "Sprinkler – plugging up to 3m",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "fire-2",
                            title: "Wet System Drain Down and Recommission - note each means 1 drain down and recommission",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "fire-3",
                            title: "Smoke Detector - relocation up to 3m",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "fire-4",
                            title: "Electronic smoke alarm isolation/de-isolation",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "fire-5",
                            title: "EWIS - relocation up to 10m with Fig. 8 cable",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "hydraulics",
            title: "Hydraulics",
            sections: [
                {
                    sectionId: "hydraulics",
                    showQty: true,
                    items: [
                        {
                            id: "hyd-1",
                            title: "Attendance including Cap and Seal Hydraulics Points - 1 water and 1 waste water",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "hyd-2",
                            title: "Cap and Seal additional hydraulics points - 1 water and 1 waste water",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "hyd-3",
                            title: "Penetration of plaster ceiling and backfill",
                            unitOptions: UNIT_OPTIONS.eachFirst,
                            selectedUnit: "each",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "hvac",
            title: "HVAC - Tenant Supplementary Units",
            sections: [
                {
                    sectionId: "hvac",
                    items: [
                        {
                            id: "hvac-1",
                            title: "Split ducted - In ceiling space air cooled FCU with external condenser",
                            unitOptions: ["lump sum", "each", "per day"],
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                        {
                            id: "hvac-2",
                            title: "Split cassette - Ceiling mounted integrated air cooled unit with external condenser",
                            unitOptions: ["lump sum", "each", "per day"],
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                        {
                            id: "hvac-3",
                            title: "Wall hung split - Wall mounted air cooled unit with external condenser",
                            unitOptions: ["lump sum", "each", "per day"],
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                        {
                            id: "hvac-4",
                            title: "Split ducted - water cooled no or internally located condenser",
                            unitOptions: ["lump sum", "each", "per day"],
                            selectedUnit: "lump sum",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "it-wall",
            title: "IT Wall",
            sections: [
                {
                    sectionId: "it-wall",
                    items: [
                        {
                            id: "it-wall-1",
                            title: "Supply and install plasterboard to perimeter IT walls",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "it-wall-2",
                            title: "Supply and install new IT wall (13mm Plasterboard either side with 92mm Stud)",
                            unitOptions: UNIT_OPTIONS.linearMetres,
                            selectedUnit: "lineal metres",
                            rate: 0,
                        },
                        {
                            id: "it-wall-3",
                            title: "Relocate EDB (Switchboard)",
                            unitOptions: UNIT_OPTIONS.perUnit,
                            selectedUnit: "per unit",
                            rate: 0,
                        },
                        {
                            id: "it-wall-4",
                            title: "Supply Paint",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "it-wall-5",
                            title: "Relocate Hydraulic Services (underground trenching)",
                            unitOptions: UNIT_OPTIONS.perUnit,
                            selectedUnit: "per unit",
                            rate: 0,
                        },
                        {
                            id: "it-wall-6",
                            title: "Relocate Hydraulic Services (above ground)",
                            unitOptions: UNIT_OPTIONS.perUnit,
                            selectedUnit: "per unit",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
    ];
}
function getDefaultInternalDemolition() {
    return [
        {
            id: "kiosks",
            title: "Kiosks",
            sections: [
                {
                    sectionId: "kiosks",
                    items: [
                        {
                            id: "kiosks-1",
                            title: "Specialty",
                            unitOptions: ["m2", "lump sum", "each"],
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "kiosks-2",
                            title: "Food & Beverage",
                            unitOptions: ["m2", "lump sum", "each"],
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "kiosks-3",
                            title: "Make Good of Mall Tiles",
                            unitOptions: ["m2", "lump sum", "each"],
                            selectedUnit: "m2",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "joinery-fixtures",
            title: "Joinery & Fixtures",
            sections: [
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (0-500m2)",
                    items: [
                        {
                            id: "joinery-0-500-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-0-500-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-0-500-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-0-500-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (500-1000m2)",
                    items: [
                        {
                            id: "joinery-500-1000-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-500-1000-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-500-1000-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-500-1000-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (1000m2+)",
                    items: [
                        {
                            id: "joinery-1000-plus-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-1000-plus-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-1000-plus-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "joinery-1000-plus-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-walls",
            title: "Demolish Walls",
            sections: [
                {
                    sectionId: "walls",
                    items: [
                        {
                            id: "walls-1",
                            title: "Remove Blockwork Walls (Single Skin)",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "walls-2",
                            title: "Remove Stud IT Walls",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "walls-3",
                            title: "After Hours works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-shopfronts",
            title: "Demolish Shopfronts",
            sections: [
                {
                    sectionId: "shopfronts",
                    items: [
                        {
                            id: "shopfront-1",
                            title: "Standard glazed & MDF",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "shopfront-2",
                            title: "Heavy Duty Steel & Stone",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "shopfront-3",
                            title: "Shopfront Height Factor Std",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                        {
                            id: "shopfront-4",
                            title: "Shopfront Height Factor Hvy",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                        {
                            id: "shopfront-5",
                            title: "After Hours Works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 1.3,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "ceiling-type",
            title: "Ceiling Type",
            sections: [
                {
                    sectionId: "ceiling",
                    sectionTitle: "Ceiling Type - to include demolition of mechanical diffusers",
                    items: [
                        {
                            id: "ceiling-1",
                            title: "Open Plan Ceiling",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ceiling-2",
                            title: "Remove Ceiling Tiles & Grid",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ceiling-3",
                            title: "Remove Plaster Lining & Gridwork",
                            unitOptions: UNIT_OPTIONS.standard,
                            selectedUnit: "m2",
                            rate: 0,
                        },
                        {
                            id: "ceiling-4",
                            title: "After Hours Works Required",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
    ];
}
function getDefaultPreliminaryCost() {
    return [
        {
            id: "prelims-supervision",
            title: "Prelims & Supervision",
            sections: [
                {
                    sectionId: "prelims",
                    items: [
                        {
                            id: "prelims-1",
                            title: "Prelims & Supervision",
                            unitOptions: ["per day", "lump sum", "m2"],
                            selectedUnit: "per day",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "scissor-lift",
            title: "Scissor Lift Hire",
            sections: [
                {
                    sectionId: "scissor-lift",
                    showWeeksQty: true,
                    items: [
                        {
                            id: "scissor-1",
                            title: '"19" Electric',
                            unitOptions: ["each", "per day", "lump sum"],
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "scissor-2",
                            title: "Float",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 350,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "skip-hire",
            title: "Skip Hire",
            sections: [
                {
                    sectionId: "skip-hire",
                    showNumberQty: true,
                    items: [
                        {
                            id: "skip-1",
                            title: "12m3",
                            unitOptions: ["each", "per day", "lump sum"],
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "skip-2",
                            title: "15m3",
                            unitOptions: ["each", "per day", "lump sum"],
                            selectedUnit: "each",
                            rate: 0,
                        },
                        {
                            id: "skip-3",
                            title: "23m3",
                            unitOptions: ["each", "per day", "lump sum"],
                            selectedUnit: "each",
                            rate: 0,
                        },
                    ],
                },
            ],
        },
        {
            id: "other-fees",
            title: "Other Fees Price on Application",
            sections: [
                {
                    sectionId: "other-fees",
                    items: [
                        {
                            id: "fees-1",
                            title: "Specialist consultant - Electrical",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                        {
                            id: "fees-2",
                            title: "Specialist consultant - Hydraulic",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                        {
                            id: "fees-3",
                            title: "Specialist consultant - Structural",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                        {
                            id: "fees-4",
                            title: "Protection works",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                        {
                            id: "fees-5",
                            title: "Specialist Cleaning",
                            unitOptions: [],
                            selectedUnit: "",
                            rate: 0,
                            hasEmptyUnit: true,
                        },
                    ],
                },
            ],
        },
    ];
}
